// /modules/KnowledgeGraphDashboard2.js
// Knowledge Graph Audit Dashboard - REBUILT Jun 2026
// Matrix table layout: source rows × signal columns, drawer detail per source
// Matches ContentHealthTable styling conventions
//
// FIXED (Jun 2026): Backend rollup rewrite changed the saved payload shape from
//   { results: [...] } to { rolledUpKGIS, rolledUpBreakdown, entities: [...], summary }.
//   This component was still reading auditData.results[0], which no longer exists,
//   so `result` was always null and the dashboard silently fell into the
//   "No audit data yet" empty state even when valid data was saved.
//   Now reads the company entity from auditData.entities and uses the backend's
//   rolledUpKGIS / rolledUpBreakdown as the canonical score instead of recalculating
//   client-side (the old local calculateKGIS() was also stale — missing the industry
//   bucket and still capped at /75 instead of the backend's /100 scale).

import React, { useState, useEffect } from 'react';

if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);
  }
}

const colors = {
  primary: '#39393a',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};

const CORE_PROPERTIES = [
  { label: 'Official website', pid: 'P856', description: 'Your primary domain' },
  { label: 'Country',          pid: 'P17',  description: 'Country of operation' },
  { label: 'Industry',         pid: 'P452', description: 'Sector classification' },
  { label: 'Founded date',     pid: 'P571', description: 'Year established' },
  { label: 'Headquarters',     pid: 'P159', description: 'Primary office location' },
  { label: 'Founder',          pid: 'P112', description: 'Founding individual(s)' },
  { label: 'Logo',             pid: 'P154', description: 'Brand logo image' },
  { label: 'CEO',              pid: 'P169', description: 'Current chief executive' }
];

const SOCIAL_PROPERTIES = [
  { label: 'LinkedIn',  pid: 'P4264',  description: 'Company page' },
  { label: 'Instagram', pid: 'P2003',  description: 'Instagram profile' },
  { label: 'Facebook',  pid: 'P2013',  description: 'Facebook page' },
  { label: 'Twitter',   pid: 'P2002',  description: 'Twitter/X profile' },
  { label: 'Bluesky',   pid: 'P12361', description: 'Bluesky profile' }
];

// ── Banding — same as ContentHealthTable ────────────────────────────────────

function band(score, max) {
  const pct = score / max * 100;
  if (pct >= 70) return { bg: 'rgba(1, 121, 111, 0.08)', text: '#01796f', bar: '#01796f' };
  if (pct >= 45) return { bg: 'rgba(233, 196, 106, 0.15)', text: '#854F0B', bar: '#e9c46a' };
  return { bg: 'rgba(231, 111, 81, 0.08)', text: '#993C1D', bar: '#e76f51' };
}

function ScoreCell({ score, max }) {
  const b = band(score, max);
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: '44px', padding: '3px 8px', borderRadius: '6px',
      backgroundColor: b.bg, color: b.text, fontSize: '12px', fontWeight: '600'
    }}>
      {score}/{max}
    </div>
  );
}

function PresenceCell({ found }) {
  if (found === null || found === undefined) {
    return <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '36px', padding: '3px 7px', borderRadius: '6px', backgroundColor: '#f3f4f6', color: '#9ca3af', fontSize: '12px', fontWeight: '600' }}>—</div>;
  }
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: '36px', padding: '3px 7px', borderRadius: '6px',
      backgroundColor: found ? 'rgba(1, 121, 111, 0.08)' : 'rgba(231, 111, 81, 0.08)',
      color: found ? '#01796f' : '#993C1D', fontSize: '13px', fontWeight: '700'
    }}>
      {found ? '✓' : '✗'}
    </div>
  );
}

// ── Drawer ───────────────────────────────────────────────────────────────────

function Drawer({ open, onClose, title, badge, badgeBg, badgeColor, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div role="dialog" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(580px, 92vw)', backgroundColor: 'white', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: 1001, display: 'flex', flexDirection: 'column', fontFamily: "'Work Sans', sans-serif" }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '17px', fontWeight: '700', color: colors.primary }}>{title}</span>
            {badge && <span style={{ padding: '3px 10px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, fontSize: '12px', fontWeight: '700' }}>{badge}</span>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ── Drawer bodies ─────────────────────────────────────────────────────────────

function WikipediaDrawer({ data }) {
  if (!data?.found) return (
    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: 'rgba(231, 111, 81, 0.05)', border: `1px solid ${colors.grey}` }}>
      <div style={{ fontWeight: '600', color: '#993C1D', marginBottom: '6px', fontSize: '13px' }}>No Wikipedia article found</div>
      <div style={{ fontSize: '13px', color: colors.primary }}>If your brand meets Wikipedia's notability criteria, creating an article is one of the highest-impact GEO actions available. LLMs heavily weight Wikipedia as a training source.</div>
    </div>
  );
  const d = data.data || {};
  const wordBand = d.wordCount > 1000 ? { text: '#01796f', bg: 'rgba(1,121,111,0.05)', border: '#01796f' }
    : d.wordCount > 500 ? { text: '#854F0B', bg: 'rgba(233,196,106,0.08)', border: '#e9c46a' }
    : { text: '#993C1D', bg: 'rgba(231,111,81,0.05)', border: colors.grey };
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Word count', value: d.wordCount?.toLocaleString() || '—', color: wordBand.text, bg: wordBand.bg, border: wordBand.border },
          { label: 'Citations', value: d.citations || '—', color: d.citations > 10 ? '#01796f' : d.citations > 5 ? '#854F0B' : '#993C1D', bg: d.citations > 10 ? 'rgba(1,121,111,0.05)' : d.citations > 5 ? 'rgba(233,196,106,0.08)' : 'rgba(231,111,81,0.05)', border: d.citations > 10 ? '#01796f' : d.citations > 5 ? '#e9c46a' : colors.grey },
          { label: 'Status', value: 'Found', color: '#01796f', bg: 'rgba(1,121,111,0.05)', border: '#01796f' }
        ].map(s => (
          <div key={s.label} style={{ padding: '12px', backgroundColor: s.bg, borderRadius: '8px', border: `1px solid ${s.border}` }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: s.color, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      {d.url && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '6px' }}>Article URL</div>
          <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#048ba8', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{d.url}</a>
        </div>
      )}
      {d.extract && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Article extract</div>
          <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: colors.primary, lineHeight: '1.6' }}>{d.extract}</div>
        </div>
      )}
      <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(244,162,97,0.08)', border: '1px solid #f4a261', fontSize: '12px', color: '#7C3200' }}>
        💡 Aim for 1,000+ words and 10+ citations to maximise your Wikipedia scoring contribution to KGIS.
      </div>
    </>
  );
}

function WikidataDrawer({ data }) {
  const keyProps = data?.data?.keyProperties || [];
  const coreFound = CORE_PROPERTIES.filter(p => keyProps.includes(p.label));
  const coreMissing = CORE_PROPERTIES.filter(p => !keyProps.includes(p.label));
  const socialFound = SOCIAL_PROPERTIES.filter(p => keyProps.includes(p.label));

  if (!data?.found) return (
    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: 'rgba(231, 111, 81, 0.05)', border: `1px solid ${colors.grey}` }}>
      <div style={{ fontWeight: '600', color: '#993C1D', marginBottom: '6px', fontSize: '13px' }}>No Wikidata entry found</div>
      <div style={{ fontSize: '13px', color: colors.primary, lineHeight: '1.6' }}>Creating a Wikidata entry is the single highest-impact GEO action available. Wikidata directly feeds Wikipedia infoboxes, Google Knowledge Graph, and is a primary training source for all major LLMs.</div>
    </div>
  );

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
        <div style={{ padding: '12px', backgroundColor: coreFound.length >= 6 ? 'rgba(1,121,111,0.05)' : coreFound.length >= 4 ? 'rgba(233,196,106,0.08)' : 'rgba(231,111,81,0.05)', borderRadius: '8px', border: `1px solid ${coreFound.length >= 6 ? '#01796f' : coreFound.length >= 4 ? '#e9c46a' : colors.grey}` }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: coreFound.length >= 6 ? '#01796f' : coreFound.length >= 4 ? '#854F0B' : '#993C1D', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Core fields</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: coreFound.length >= 6 ? '#01796f' : coreFound.length >= 4 ? '#854F0B' : '#993C1D' }}>{coreFound.length}/{CORE_PROPERTIES.length}</div>
        </div>
        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Social profiles</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: colors.primary }}>{socialFound.length}/{SOCIAL_PROPERTIES.length}</div>
        </div>
        <div style={{ padding: '12px', backgroundColor: 'rgba(1,121,111,0.05)', borderRadius: '8px', border: '1px solid #01796f' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#01796f', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#01796f' }}>Found</div>
        </div>
      </div>

      {data?.data?.entityId && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '6px' }}>Wikidata entity</div>
          <a href={`https://www.wikidata.org/wiki/${data.data.entityId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#048ba8', fontFamily: 'ui-monospace, monospace' }}>wikidata.org/wiki/{data.data.entityId}</a>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '10px' }}>Core properties</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {CORE_PROPERTIES.map(prop => {
            const found = keyProps.includes(prop.label);
            return (
              <div key={prop.pid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '8px', backgroundColor: found ? 'rgba(1,121,111,0.05)' : 'rgba(231,111,81,0.05)', border: `1px solid ${found ? '#01796f' : colors.grey}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: found ? '#01796f' : colors.grey, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{prop.label}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{prop.description}</div>
                  </div>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '700', color: found ? '#01796f' : '#993C1D' }}>{found ? '✓' : '✗'}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '10px' }}>Social profiles <span style={{ fontSize: '11px', fontWeight: '400', color: '#6b7280' }}>(advisory — not scored)</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {SOCIAL_PROPERTIES.map(prop => {
            const found = keyProps.includes(prop.label);
            return (
              <div key={prop.pid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: found ? '#01796f' : '#d1d5db', flexShrink: 0 }} />
                  <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{prop.label}</div>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '700', color: found ? '#01796f' : '#9ca3af' }}>{found ? '✓' : '✗'}</span>
              </div>
            );
          })}
        </div>
      </div>

      {coreMissing.length > 0 && (
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(244,162,97,0.08)', border: '1px solid #f4a261', fontSize: '12px', color: '#7C3200' }}>
          💡 Missing core fields: <strong>{coreMissing.map(p => p.label).join(', ')}</strong>. Add these to your Wikidata entry to improve KGIS.
        </div>
      )}
    </>
  );
}

function GoogleKGDrawer({ data }) {
  if (!data?.found) return (
    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: 'rgba(231, 111, 81, 0.05)', border: `1px solid ${colors.grey}` }}>
      <div style={{ fontWeight: '600', color: '#993C1D', marginBottom: '6px', fontSize: '13px' }}>Not found in Google Knowledge Graph</div>
      <div style={{ fontSize: '13px', color: colors.primary, lineHeight: '1.6' }}>Google KG typically populates automatically once a complete Wikidata entry exists. You cannot submit directly — focus on Wikidata completeness first.</div>
    </div>
  );
  const d = data.data || {};
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
        <div style={{ padding: '12px', backgroundColor: 'rgba(1,121,111,0.05)', borderRadius: '8px', border: '1px solid #01796f' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#01796f', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#01796f' }}>Found</div>
        </div>
        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Result score</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: colors.primary }}>{d.resultScore ? Math.round(d.resultScore).toLocaleString() : '—'}</div>
        </div>
      </div>
      {d.name && <div style={{ marginBottom: '12px' }}><div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>Name in KG</div><div style={{ fontSize: '14px', color: colors.primary, fontWeight: '500' }}>{d.name}</div></div>}
      {d.description && <div style={{ marginBottom: '12px' }}><div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>Description</div><div style={{ fontSize: '13px', color: colors.primary, lineHeight: '1.6', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>{d.description}</div></div>}
      {d.url && <div style={{ marginBottom: '16px' }}><div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>URL in KG</div><a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#048ba8', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{d.url}</a></div>}
      <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(244,162,97,0.08)', border: '1px solid #f4a261', fontSize: '12px', color: '#7C3200' }}>
        💡 Google KG presence is worth 15 pts in KGIS. You cannot optimise it directly — it reflects your Wikidata and Wikipedia completeness.
      </div>
    </>
  );
}

function IndustryDrawer({ entity }) {
  const cb = entity?.crunchbase;
  const ch = entity?.companiesHouse;
  const oc = entity?.openCorporates;
  const cbFound = !cb?.skip && !!cb?.found;
  const chFound = !ch?.skip && !!ch?.found;
  const ocFound = !oc?.skip && !!oc?.found;
  const registryFound = chFound || ocFound;

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
        <div style={{ padding: '12px', backgroundColor: cbFound ? 'rgba(1,121,111,0.05)' : 'rgba(231,111,81,0.05)', borderRadius: '8px', border: `1px solid ${cbFound ? '#01796f' : colors.grey}` }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: cbFound ? '#01796f' : '#993C1D', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Crunchbase</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: cbFound ? '#01796f' : '#993C1D' }}>{cbFound ? 'Found' : 'Not found'}</div>
        </div>
        <div style={{ padding: '12px', backgroundColor: registryFound ? 'rgba(1,121,111,0.05)' : 'rgba(231,111,81,0.05)', borderRadius: '8px', border: `1px solid ${registryFound ? '#01796f' : colors.grey}` }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: registryFound ? '#01796f' : '#993C1D', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Companies House / OpenCorporates</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: registryFound ? '#01796f' : '#993C1D' }}>{registryFound ? 'Found' : 'Not found'}</div>
        </div>
      </div>
      {cb?.url && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '6px' }}>Crunchbase profile</div>
          <a href={cb.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#048ba8', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{cb.url}</a>
        </div>
      )}
      <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(244,162,97,0.08)', border: '1px solid #f4a261', fontSize: '12px', color: '#7C3200' }}>
        💡 Industry sources are worth up to 25 pts (10 Crunchbase + 10 registry + 5 bonus for both). These registries are scraped by LLM training pipelines and strengthen entity disambiguation.
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const KnowledgeGraphDashboard = ({
  campaignId,
  userId,
  campaignName,
  urls,
  apiBaseUrl = `${process.env.REACT_APP_API_URL || 'https://geojetpack-production.up.railway.app'}/api`
}) => {
  const [auditData, setAuditData]               = useState(null);
  const [isProcessing, setIsProcessing]         = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError]                       = useState(null);
  const [lastAuditDate, setLastAuditDate]       = useState(null);
  const [drawer, setDrawer]                     = useState(null); // 'wikipedia' | 'wikidata' | 'googleKG' | 'industry'

  useEffect(() => {
    if (campaignId) loadLatestResults();
    else setIsInitialLoading(false);
  }, [campaignId]);

  const loadLatestResults = async () => {
    setIsInitialLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/audit-results/latest?campaignId=${campaignId}&moduleType=knowledge-graph`);
      if (res.ok) {
        const data = await res.json();
        if (data?.results?.length > 0) {
          // data.results[0] here is the audit_results TABLE ROW (id, url, result_data, created_at...).
          // result_data is the KG rollup payload itself — keep these two concepts separate.
          const row = data.results[0];
          const parsed = typeof row.result_data === 'string'
            ? JSON.parse(row.result_data)
            : row.result_data;
          setAuditData(parsed);
          setLastAuditDate(row.created_at);
        }
      }
    } catch (err) {
      console.error('Error loading KG results:', err);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const runKGAudit = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/modules/knowledge-graph-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, userId, urls, save: true })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'KG audit failed');
      if (data.success) {
        // POST response shape: { success: true, data: { rolledUpKGIS, rolledUpBreakdown, entities, summary } }
        setAuditData(data.data);
        setLastAuditDate(new Date().toISOString());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTimeSince = (ts) => {
    if (!ts) return null;
    const diff = Date.now() - new Date(ts);
    const mins = Math.floor(diff / 60000);
    const hrs  = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hrs > 0)  return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
    if (mins > 0) return `${mins} minute${mins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (isInitialLoading) return (
    <div style={{ padding: '48px', textAlign: 'center', fontFamily: "'Work Sans', sans-serif" }}>
      <div style={{ width: '36px', height: '36px', border: '4px solid #e5e7eb', borderTop: `4px solid ${colors.green}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading Knowledge Graph audit...</p>
      <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
    </div>
  );

  // ── Shape fix: backend now returns { rolledUpKGIS, rolledUpBreakdown, entities: [...], summary }
  // instead of the old { results: [...] }. The "company" entity (role === 'company') carries the
  // per-source wikipedia/wikidata/googleKG/crunchbase/companiesHouse/openCorporates objects that
  // the drawers and matrix table read from. Fall back to entities[0] defensively in case role is
  // ever missing on older saved rows.
  const company = auditData?.entities?.find(e => e.role === 'company') || auditData?.entities?.[0] || null;

  // Canonical score now comes straight from the backend rollup — it already accounts for the
  // full entity set (company + products) and the industry bucket, which the old local
  // calculateKGIS() never included. Scale is /100, not /75.
  const kgisTotal = auditData?.rolledUpKGIS ?? 0;
  const kgisBreakdown = auditData?.rolledUpBreakdown ?? { wikipedia: 0, wikidata: 0, googleKG: 0, industry: 0 };

  const kgisB = band(kgisTotal, 100);

  const SOURCES = company ? [
    {
      key: 'wikipedia',
      label: 'Wikipedia',
      favicon: 'https://en.wikipedia.org/favicon.ico',
      found: company.wikipedia?.found ?? null,
      score: kgisBreakdown.wikipedia,
      max: 25,
      signal: company.wikipedia?.found ? `${company.wikipedia.data?.wordCount?.toLocaleString() || '—'} words` : 'No article',
      secondary: company.wikipedia?.found ? `${company.wikipedia.data?.citations || 0} citations` : '—',
      gap: company.wikipedia?.found
        ? ((company.wikipedia.data?.wordCount || 0) < 1000 ? 'Expand article' : null)
        : 'Create article'
    },
    {
      key: 'wikidata',
      label: 'Wikidata',
      favicon: 'https://www.wikidata.org/favicon.ico',
      found: company.wikidata?.found ?? null,
      score: kgisBreakdown.wikidata,
      max: 35,
      signal: company.wikidata?.found
        ? `${CORE_PROPERTIES.filter(p => (company.wikidata.data?.keyProperties || []).includes(p.label)).length}/${CORE_PROPERTIES.length} core fields`
        : 'No entry',
      secondary: company.wikidata?.data?.entityId || '—',
      gap: (() => {
        if (!company.wikidata?.found) return 'Create entry';
        const missing = CORE_PROPERTIES.filter(p => !(company.wikidata.data?.keyProperties || []).includes(p.label));
        return missing.length > 0 ? `Add: ${missing.slice(0, 2).map(p => p.label).join(', ')}${missing.length > 2 ? ` +${missing.length - 2}` : ''}` : null;
      })()
    },
    {
      key: 'googleKG',
      label: 'Google KG',
      favicon: 'https://www.google.com/favicon.ico',
      found: company.googleKG?.found ?? null,
      score: kgisBreakdown.googleKG,
      max: 15,
      signal: company.googleKG?.found ? `Score: ${company.googleKG.data?.resultScore ? Math.round(company.googleKG.data.resultScore).toLocaleString() : '—'}` : 'Not indexed',
      secondary: company.googleKG?.data?.name || '—',
      gap: !company.googleKG?.found ? 'Complete Wikidata first' : null
    },
    {
      key: 'industry',
      label: 'Industry Sources',
      favicon: null,
      found: (!company.crunchbase?.skip && company.crunchbase?.found) || (!company.companiesHouse?.skip && company.companiesHouse?.found) || (!company.openCorporates?.skip && company.openCorporates?.found) || false,
      score: kgisBreakdown.industry,
      max: 25,
      signal: (!company.crunchbase?.skip && company.crunchbase?.found) ? 'Crunchbase found' : 'Not found',
      secondary: ((!company.companiesHouse?.skip && company.companiesHouse?.found) || (!company.openCorporates?.skip && company.openCorporates?.found)) ? 'Registry confirmed' : '—',
      gap: kgisBreakdown.industry < 25 ? 'Register on industry directories' : null
    }
  ] : [];

  return (
    <div style={{ fontFamily: "'Work Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '2px' }}>Knowledge Graph</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
            {campaignName || 'Campaign'} · Entity-level rollup{auditData?.summary?.entityCount > 1 ? ` (${auditData.summary.entityCount} entities)` : ''} · {lastAuditDate ? `Updated ${formatTimeSince(lastAuditDate)}` : 'Not yet run'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {company && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>KGIS Score</div>
              <div style={{ fontSize: '26px', fontWeight: '700', color: kgisB.text }}>{kgisTotal}<span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/100</span></div>
            </div>
          )}
          <button
            onClick={runKGAudit}
            disabled={isProcessing}
            style={{ padding: '10px 20px', backgroundColor: isProcessing ? '#e5e7eb' : colors.green, color: 'white', border: 'none', borderRadius: '8px', cursor: isProcessing ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'Work Sans', sans-serif" }}>
            {isProcessing ? 'Running...' : auditData ? 'Re-run' : 'Run Audit'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: 'rgba(231,111,81,0.05)', border: `1px solid ${colors.grey}`, borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#993C1D' }}>
          {error}
        </div>
      )}

      {/* Empty state */}
      {!auditData && !isProcessing && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>No audit data yet</h3>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Run the Knowledge Graph audit to check your entity presence across Wikipedia, Wikidata, Google KG and industry directories.</p>
        </div>
      )}

      {auditData && company && (
        <>
          {/* Score tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {SOURCES.map(src => {
              const b = band(src.score, src.max);
              return (
                <div key={src.key} style={{ backgroundColor: 'white', border: '0.5px solid #e5e7eb', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {src.favicon
                      ? <img src={src.favicon} alt={src.label} style={{ width: '14px', height: '14px' }} onError={e => e.target.style.opacity = 0} />
                      : <i className="ti ti-building" style={{ fontSize: '14px', color: '#9ca3af' }} />
                    }
                    <span style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>{src.label}</span>
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '500', color: b.text }}>
                    {src.score}/{src.max}
                  </div>
                  <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: `${(src.score / src.max) * 100}%`, height: '100%', background: b.bar }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Matrix table */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '500px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {[
                      { label: 'Source', width: '160px', align: 'left' },
                      { label: 'Present', align: 'center' },
                      { label: 'Score', align: 'center' },
                      { label: 'Key signal', align: 'left' },
                      { label: 'Secondary', align: 'left' },
                      { label: 'Top gap', align: 'left' }
                    ].map((col, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: col.align || 'left', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderLeft: i > 0 ? '1px solid #f3f4f6' : 'none', width: col.width }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SOURCES.map((src, idx) => (
                    <tr
                      key={src.key}
                      style={{ borderBottom: idx < SOURCES.length - 1 ? '1px solid #f3f4f6' : 'none', backgroundColor: 'white', cursor: 'pointer' }}
                      onClick={() => setDrawer(src.key)}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; }}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {src.favicon
                            ? <img src={src.favicon} alt={src.label} style={{ width: '16px', height: '16px' }} onError={e => e.target.style.opacity = 0} />
                            : <i className="ti ti-building" style={{ fontSize: '15px', color: '#9ca3af' }} />
                          }
                          <span style={{ fontWeight: '600', color: colors.primary }}>{src.label}</span>
                        </div>
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'center', borderLeft: '1px solid #f3f4f6' }}>
                        <PresenceCell found={src.found} />
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'center', borderLeft: '1px solid #f3f4f6' }}>
                        <ScoreCell score={src.score} max={src.max} />
                      </td>
                      <td style={{ padding: '8px 16px', borderLeft: '1px solid #f3f4f6', color: colors.primary, fontSize: '13px' }}>
                        {src.signal}
                      </td>
                      <td style={{ padding: '8px 16px', borderLeft: '1px solid #f3f4f6', color: '#6b7280', fontSize: '12px', fontFamily: src.key === 'wikidata' ? 'ui-monospace, monospace' : 'inherit' }}>
                        {src.secondary}
                      </td>
                      <td style={{ padding: '8px 16px', borderLeft: '1px solid #f3f4f6' }}>
                        {src.gap
                          ? <span style={{ fontSize: '12px', color: '#993C1D', backgroundColor: 'rgba(231,111,81,0.08)', padding: '3px 8px', borderRadius: '4px', fontWeight: '500' }}>{src.gap}</span>
                          : <span style={{ fontSize: '12px', color: '#01796f' }}>✓ No gaps</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {[
                { label: 'Strong (≥70%)', bg: 'rgba(1,121,111,0.08)', color: '#01796f' },
                { label: 'Adequate (45–69%)', bg: 'rgba(233,196,106,0.15)', color: '#854F0B' },
                { label: 'Needs work (<45%)', bg: 'rgba(231,111,81,0.08)', color: '#993C1D' }
              ].map(({ label, bg, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                  <div style={{ width: '24px', height: '16px', borderRadius: '4px', backgroundColor: bg, border: `1px solid ${color}20` }} />
                  <span>{label}</span>
                </div>
              ))}
              <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>Click a row to see full detail</div>
            </div>
          </div>

          {/* Priority actions */}
          {(() => {
            const actions = [];
            if (!company.wikidata?.found) actions.push('Create a Wikidata entry — highest single impact action for KGIS');
            if (!company.wikipedia?.found) actions.push('Create a Wikipedia article if your brand meets notability criteria');
            if (company.wikidata?.found) {
              const keyProps = company.wikidata.data?.keyProperties || [];
              const missing = CORE_PROPERTIES.filter(p => !keyProps.includes(p.label));
              if (missing.length > 0) actions.push(`Add missing Wikidata core fields: ${missing.map(p => p.label).join(', ')}`);
            }
            if (!company.googleKG?.found && company.wikidata?.found) actions.push('Google KG should populate automatically once Wikidata is complete');
            if (company.wikipedia?.found && (company.wikipedia.data?.wordCount || 0) < 1000) actions.push(`Expand Wikipedia article — currently ${company.wikipedia.data?.wordCount || 0} words, aim for 1,000+`);
            if (kgisBreakdown.industry < 25) actions.push('Register on industry directories (Crunchbase, Companies House/OpenCorporates)');
            if (actions.length === 0) return null;
            return (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: `1px solid rgba(233,196,106,0.4)` }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '12px' }}>Priority actions</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {actions.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#854F0B', backgroundColor: 'rgba(233,196,106,0.15)', padding: '2px 7px', borderRadius: '4px', flexShrink: 0, marginTop: '1px' }}>{i + 1}</span>
                      <span style={{ fontSize: '13px', color: colors.primary, lineHeight: '1.5' }}>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Drawers */}
      <Drawer
        open={drawer === 'wikipedia'}
        onClose={() => setDrawer(null)}
        title="Wikipedia"
        badge={company?.wikipedia?.found ? `${kgisBreakdown.wikipedia}/25 pts` : 'Not found'}
        badgeBg={company?.wikipedia?.found ? band(kgisBreakdown.wikipedia, 25).bg : 'rgba(231,111,81,0.08)'}
        badgeColor={company?.wikipedia?.found ? band(kgisBreakdown.wikipedia, 25).text : '#993C1D'}
      >
        <WikipediaDrawer data={company?.wikipedia} />
      </Drawer>

      <Drawer
        open={drawer === 'wikidata'}
        onClose={() => setDrawer(null)}
        title="Wikidata"
        badge={company?.wikidata?.found ? `${kgisBreakdown.wikidata}/35 pts` : 'Not found'}
        badgeBg={company?.wikidata?.found ? band(kgisBreakdown.wikidata, 35).bg : 'rgba(231,111,81,0.08)'}
        badgeColor={company?.wikidata?.found ? band(kgisBreakdown.wikidata, 35).text : '#993C1D'}
      >
        <WikidataDrawer data={company?.wikidata} />
      </Drawer>

      <Drawer
        open={drawer === 'googleKG'}
        onClose={() => setDrawer(null)}
        title="Google Knowledge Graph"
        badge={company?.googleKG?.found ? `${kgisBreakdown.googleKG}/15 pts` : 'Not found'}
        badgeBg={company?.googleKG?.found ? band(kgisBreakdown.googleKG, 15).bg : 'rgba(231,111,81,0.08)'}
        badgeColor={company?.googleKG?.found ? band(kgisBreakdown.googleKG, 15).text : '#993C1D'}
      >
        <GoogleKGDrawer data={company?.googleKG} />
      </Drawer>

      <Drawer
        open={drawer === 'industry'}
        onClose={() => setDrawer(null)}
        title="Industry Sources"
        badge={`${kgisBreakdown.industry}/25 pts`}
        badgeBg={band(kgisBreakdown.industry, 25).bg}
        badgeColor={band(kgisBreakdown.industry, 25).text}
      >
        <IndustryDrawer entity={company} />
      </Drawer>
    </div>
  );
};

export default KnowledgeGraphDashboard;
